import { useCallback } from 'react';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useProRataAmount from 'features/promotions/hooks/useProRataAmount';
import { useAppSelector } from 'store/hooks';
import { selectUserIsRefundable } from '../selectors/tier';
import useDowngradeOffer from './useDowngradeOffer';
import { selectAbFairPrice, selectDidPayFullPrice } from '../selectors';
import useNavigateToDiscountStep from './useNavigateToDiscountStep';
const useNavigateToPartialRefund = (tier, loadOnMount) => {
    const navigation = useMainStackNavigation();
    const { downgradeDiscountDataRef, fairPriceOffersRef, isFetching: isFetchingDowngrade, retry: retryDowngrade, } = useDowngradeOffer(loadOnMount);
    const abFairPrice = useAppSelector(selectAbFairPrice);
    const { navigateToDiscountStep, isFetching: isFetchingCancellation } = useNavigateToDiscountStep(tier);
    const { getPreview, isFetchingStripePreview } = useProRataAmount();
    const isRefundable = !!useAppSelector(selectUserIsRefundable);
    const isPaidFullPrice = useAppSelector(selectDidPayFullPrice);
    const isFetching = isFetchingStripePreview || isFetchingDowngrade || isFetchingCancellation;
    const fetchDowngradeDiscounts = useCallback(async () => {
        if (downgradeDiscountDataRef.current?.length) {
            return downgradeDiscountDataRef.current;
        }
        await retryDowngrade();
        return downgradeDiscountDataRef.current?.length
            ? downgradeDiscountDataRef.current
            : null;
    }, [downgradeDiscountDataRef, retryDowngrade]);
    const fetchPreviewData = useCallback(async (discountData) => {
        if (!discountData.length || discountData[0].type !== 'stripe') {
            return null;
        }
        const { subscription, discount } = discountData[0];
        return getPreview(subscription.id, discount);
    }, [getPreview]);
    const getDiscountAndPartialRefundPreview = useCallback(async () => {
        const downgradeDiscounts = await fetchDowngradeDiscounts();
        if (!downgradeDiscounts) {
            return null;
        }
        const discountedPreview = await fetchPreviewData(downgradeDiscounts);
        if (discountedPreview) {
            return { discountData: downgradeDiscounts, discountedPreview };
        }
        return null;
    }, [fetchDowngradeDiscounts, fetchPreviewData]);
    const navigateToDowngradeOffer = useCallback(async (callback, source, discountDataAndPreview, fetchDataAndPreview, skipScheduledOffer) => {
        let discountAndPreview = discountDataAndPreview;
        if (!discountAndPreview && fetchDataAndPreview) {
            discountAndPreview = await getDiscountAndPartialRefundPreview();
        }
        // The partial refund is only offered in the downgrade and refund flows
        const skipPartialRefund = source === 'unsubscribe';
        if (!skipPartialRefund &&
            discountAndPreview?.discountData &&
            discountAndPreview.discountedPreview?.refundableToday &&
            isPaidFullPrice &&
            isRefundable) {
            const { discountData: discountDataParams, discountedPreview } = discountAndPreview;
            navigation.navigateDeprecated('ManagePremiumSubscriptionPartialRefund', {
                discountData: discountDataParams,
                tier,
                preview: discountedPreview,
                callback,
                source,
            });
            return;
        }
        if (!skipScheduledOffer && abFairPrice && fairPriceOffersRef.current) {
            navigation.navigate('ManagePremiumSubscriptionFairPrice', {
                offers: fairPriceOffersRef.current.fairPriceOffers,
                currency: fairPriceOffersRef.current.currency,
                tier,
                source: 'downgrade',
                callback,
            });
            return;
        }
        if (discountAndPreview?.discountData && !skipScheduledOffer) {
            const { discountData: discountDataParams } = discountAndPreview;
            navigation.navigateDeprecated('ManagePremiumSubscriptionDowngradeDiscount', {
                discountData: discountDataParams,
                tier,
                callback,
                source,
            });
            return;
        }
        // cancellation offer
        if (skipScheduledOffer) {
            navigateToDiscountStep();
            return;
        }
        callback();
    }, [
        isPaidFullPrice,
        isRefundable,
        abFairPrice,
        fairPriceOffersRef,
        getDiscountAndPartialRefundPreview,
        tier,
        navigateToDiscountStep,
    ]);
    return {
        isFetching,
        getDiscountAndPartialRefundPreview,
        navigateToDowngradeOffer,
    };
};
export default useNavigateToPartialRefund;
